import React, { useState } from 'react';
import { Phone, AlertTriangle, X, Heart, Stethoscope, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Always-visible Emergency Button for families. One tap reveals 3 calm
 * options: Call 911, talk to a CaraLoom nurse now, or message Cara.
 */
export const EmergencyButton = () => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const alertNurse = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/crisis/alert`, {
        situation: 'Family activated Crisis Mode from dashboard.',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("We've alerted an on-call nurse — they'll call you within 15 minutes.");
      setTimeout(() => setOpen(false), 1500);
    } catch (e) {
      toast.error('Could not reach the alert system. If life-threatening, call 911 now.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="emergency-fab"
        aria-label="Open emergency help"
        className="fixed z-[55] bottom-5 left-5 sm:bottom-7 sm:left-7 flex items-center gap-2 pl-3 pr-4 py-3 rounded-full bg-cn-error text-white font-semibold shadow-xl shadow-cn-error/30 hover:shadow-cn-error/50 transition-all hover:-translate-y-0.5"
        style={{ backgroundColor: '#B04646' }}
      >
        <span className="relative flex h-9 w-9 rounded-full bg-white/15 items-center justify-center">
          <AlertTriangle className="w-4 h-4" />
          <span className="absolute inset-0 rounded-full ring-2 ring-white/40 animate-ping" />
        </span>
        <span className="text-sm">Emergency</span>
      </button>

      {open && (() => {
        const close = () => { if (!sending) setOpen(false); };
        return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in-up" onClick={(e) => { if (e.target === e.currentTarget) close(); }} data-testid="emergency-modal">
          <div className="relative w-full max-w-md rounded-3xl bg-white dark:bg-[#0F172A] border border-cn-border shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <EscapeKeyClose onClose={close} />
            <div className="p-6 text-white" style={{ background: 'linear-gradient(135deg, #B04646 0%, #8c3838 100%)' }}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.14em] font-bold text-white/80">Crisis Mode</p>
                    <h3 className="font-display font-extrabold text-2xl mt-0.5">We&apos;re with you</h3>
                  </div>
                </div>
                <button type="button" onClick={close} aria-label="Close" data-testid="emergency-close" className="flex items-center justify-center rounded-full hover:bg-white/15 active:bg-white/25 transition-colors touch-manipulation" style={{ minWidth: 44, minHeight: 44 }}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-white/90 mt-3">Take a breath. Pick the option that fits — we&apos;ll handle the rest.</p>
            </div>
            <div className="p-5 space-y-3">
              <a
                href="tel:911"
                data-testid="emergency-call-911"
                className="flex items-center gap-4 p-4 rounded-2xl bg-cn-error text-white font-semibold hover:opacity-95 transition-opacity"
              >
                <Phone className="w-5 h-5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-base">Call 911 now</p>
                  <p className="text-xs text-white/85 font-normal">If it&apos;s life-threatening — chest pain, can&apos;t breathe, stroke signs, severe bleeding.</p>
                </div>
              </a>
              <button
                onClick={alertNurse}
                disabled={sending}
                data-testid="emergency-call-nurse"
                className="w-full flex items-center gap-4 p-4 rounded-2xl bg-cn-primary text-white font-semibold hover:bg-cn-primary-hover disabled:opacity-60"
              >
                <Stethoscope className="w-5 h-5 flex-shrink-0" />
                <div className="flex-1 text-left">
                  <p className="text-base">{sending ? 'Alerting…' : 'Talk to a CaraLoom nurse'}</p>
                  <p className="text-xs text-white/85 font-normal">An on-call nurse will call you within 15 minutes.</p>
                </div>
              </button>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setOpen(false); document.querySelector('[data-testid="lynk-fab"]')?.click(); }}
                data-testid="emergency-open-lynk"
                className="w-full flex items-center gap-4 p-4 rounded-2xl bg-cn-bg dark:bg-white/5 border border-cn-border text-cn-text dark:text-white font-semibold hover:border-cn-primary/40"
              >
                <Heart className="w-5 h-5 flex-shrink-0 text-cn-primary" />
                <div className="flex-1 text-left">
                  <p className="text-base">Just talk to Cara</p>
                  <p className="text-xs text-cn-text-muted font-normal">Calming guidance, no urgency required.</p>
                </div>
              </a>
              <div className="flex items-center gap-2 pt-2 text-[11px] text-cn-text-muted">
                <ShieldCheck className="w-3.5 h-3.5 text-cn-success" />
                <span>Your location and key health info will be shared with whoever responds.</span>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </>
  );
};

// Tiny inline component that wires an ESC-key close. Kept inside this file
// so EmergencyButton stays self-contained; for new modals prefer the shared
// `useModalClose` hook at /app/frontend/src/hooks/useModalClose.js.
const EscapeKeyClose = ({ onClose }) => {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(e); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return null;
};

export default EmergencyButton;
